import {
  getLocalStorage,
  safeGetJsonStorageItem,
  safeGetStorageItem,
  safeRemoveStorageItem,
} from './browserStorage';

const CURRENT_AUTH_COOKIE_NAMES = ['otello_d_session_info', '_otello_session'];
const STALE_AUTH_COOKIE_NAMES = ['auth_data', 'user'];
const TRACKED_IDB_KEYS = ['otello-idb-names'];
const IDB_PREFIXES = ['otello-store-'];

const getDefaultDocument = () =>
  typeof document !== 'undefined' ? document : null;

const getDefaultNavigator = () =>
  typeof navigator !== 'undefined' ? navigator : null;

const getDefaultWindow = () => (typeof window !== 'undefined' ? window : null);

const getCookieDomains = (windowObject = getDefaultWindow()) => {
  const hostname = windowObject?.location?.hostname;

  if (!hostname) {
    return [null];
  }

  const domains = [null, hostname, `.${hostname}`];
  const parts = hostname.split('.');

  if (parts.length > 2) {
    domains.push(`.${parts.slice(-2).join('.')}`);
  }

  return [...new Set(domains)];
};

const expireCookie = ({
  documentObject = getDefaultDocument(),
  name,
  windowObject = getDefaultWindow(),
}) => {
  if (!documentObject || !name) return false;

  const paths = ['/', '/app'];

  try {
    getCookieDomains(windowObject).forEach((domain) => {
      paths.forEach((path) => {
        const domainPart = domain ? `; domain=${domain}` : '';
        documentObject.cookie = `${name}=; Max-Age=0; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=${path}${domainPart}; SameSite=Lax`;
      });
    });
    return true;
  } catch (error) {
    return false;
  }
};

export const clearClientCookies = ({
  documentObject = getDefaultDocument(),
  includeCurrentAuth = true,
  windowObject = getDefaultWindow(),
} = {}) => {
  let visibleCookieNames = [];

  try {
    visibleCookieNames =
      documentObject?.cookie
        ?.split(';')
        .map((cookie) => cookie.trim().split('=')[0])
        .filter(Boolean) || [];
  } catch (error) {
    visibleCookieNames = [];
  }

  const names = new Set(STALE_AUTH_COOKIE_NAMES);

  if (includeCurrentAuth) {
    visibleCookieNames.forEach((name) => names.add(name));
    CURRENT_AUTH_COOKIE_NAMES.forEach((name) => names.add(name));
  }

  names.forEach((name) => expireCookie({ documentObject, name, windowObject }));
};

const parseTrackedDbNames = (storage = getLocalStorage()) =>
  TRACKED_IDB_KEYS.flatMap((key) => {
    const value = safeGetJsonStorageItem(storage, key, []);

    return Array.isArray(value) ? value : [];
  });

const getLikelyDbNames = (storage = getLocalStorage()) => {
  const accountId = safeGetStorageItem(storage, 'account_id');
  if (!accountId) return [];

  return IDB_PREFIXES.map((prefix) => `${prefix}${accountId}`);
};

const deleteDatabase = (dbName, indexedDbObject) =>
  new Promise((resolve) => {
    if (!dbName || !indexedDbObject?.deleteDatabase) {
      resolve();
      return;
    }

    const request = indexedDbObject.deleteDatabase(dbName);
    request.onsuccess = resolve;
    request.onerror = resolve;
    request.onblocked = resolve;
  });

export const clearIndexedDB = async ({
  storage = getLocalStorage(),
  windowObject = getDefaultWindow(),
} = {}) => {
  const indexedDbObject = windowObject?.indexedDB;

  if (!indexedDbObject) return;

  let dbNames = [];

  try {
    if (indexedDbObject.databases) {
      const dbs = await indexedDbObject.databases();
      dbNames = dbs.map((db) => db.name).filter(Boolean);
    }
  } catch (error) {
    dbNames = [];
  }

  const trackedNames = [
    ...parseTrackedDbNames(storage),
    ...getLikelyDbNames(storage),
  ];
  const namesToDelete = [...new Set([...dbNames, ...trackedNames])].filter(
    Boolean
  );

  await Promise.all(
    namesToDelete.map((dbName) => deleteDatabase(dbName, indexedDbObject))
  );
  TRACKED_IDB_KEYS.forEach((key) => safeRemoveStorageItem(storage, key));
};

export const clearCacheStorage = async (windowObject = getDefaultWindow()) => {
  if (!windowObject?.caches) return;

  try {
    const cacheNames = await windowObject.caches.keys();
    await Promise.all(
      cacheNames.map((cacheName) => windowObject.caches.delete(cacheName))
    );
  } catch (error) {
    // Cache cleanup is best effort.
  }
};

export const unregisterServiceWorkers = async (
  navigatorObject = getDefaultNavigator()
) => {
  if (!navigatorObject?.serviceWorker) return;

  try {
    const registrations =
      await navigatorObject.serviceWorker.getRegistrations();
    await Promise.all(
      registrations.map((registration) => registration.unregister())
    );
  } catch (error) {
    // Service worker cleanup is best effort.
  }
};

export const clearBrowserCaches = async ({
  documentObject = getDefaultDocument(),
  includeCurrentAuth = true,
  navigatorObject = getDefaultNavigator(),
  storage = getLocalStorage(),
  windowObject = getDefaultWindow(),
} = {}) => {
  clearClientCookies({ documentObject, includeCurrentAuth, windowObject });
  await Promise.all([
    clearIndexedDB({ storage, windowObject }),
    clearCacheStorage(windowObject),
    unregisterServiceWorkers(navigatorObject),
  ]);
};
